import { useState, type FormEvent } from 'react';

type Status = 'idle' | 'success' | 'error';

type UseFormSubmitResult = {
  status: Status;
  submitting: boolean;
  errorMessage: string;
  handleSubmit: (event: FormEvent<HTMLFormElement>, submitAddr?: string) => Promise<void>;
};

/** Submit all KOR38 forms to the registered Netlify Forms endpoint. */
export function useFormSubmit(): UseFormSubmitResult {
  const [status, setStatus] = useState<Status>('idle');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>, _submitAddr?: string) => {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    const formName = String(formData.get('form-name') || form.getAttribute('name') || '');
    if (!formName) {
      setStatus('error');
      setErrorMessage('The form could not be identified. Please try again later.');
      return;
    }
    formData.set('form-name', formName);

    // Record explicit consent choices even when an optional checkbox is unticked.
    // Read actual checkbox state rather than treating a missing value as consent.
    for (const field of ['marketing_consent', 'contact_consent', 'privacy_ack']) {
      const checkbox = form.querySelector<HTMLInputElement>(`input[type="checkbox"][name="${field}"]`);
      if (checkbox) {
        formData.set(field, checkbox.checked ? 'Yes' : 'No');
      }
    }

    // Do not send submissions completed by automated bots.
    if (String(formData.get('website_alt') || '').trim()) {
      setStatus('error');
      setErrorMessage('We could not send your enquiry. Please try again.');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrorMessage('');
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as URLSearchParams).toString(),
      });
      if (!response.ok) {
        throw new Error(`Netlify submission returned ${response.status}`);
      }
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setErrorMessage('We could not send your enquiry just now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return { status, submitting, errorMessage, handleSubmit };
}
